import React, { useState, useRef, useEffect } from "react";
import api, { photoUrl } from "./api";
import { useContent } from "./content";
import { GOLD, tf } from "./data";

// One MediaItem renders an image or video with optional caption + highlight badge.
// In admin mode it also exposes inline caption edit, highlight selector and delete.
export function MediaItem({
  item,
  isAdmin,
  onUpdate,
  onDelete,
  size = "thumb",          // "thumb" | "large" | "masonry"
  day,                     // optional Day object so highlight selector knows the options
  showHighlightBadge = true,
  onClick,
}) {
  const isVideo = (item.type === "video") || (item.mime || "").startsWith("video/");
  const src = photoUrl(item.url);
  const highlight =
    item.highlight_index != null && day && day.highlights
      ? day.highlights[item.highlight_index]
      : null;

  return (
    <div className={`jp-media jp-media-${size}`} data-testid={`media-${item.id}`}>
      <div className="jp-media-frame" onClick={onClick}>
        {isVideo ? (
          <video
            src={src}
            controls
            preload="metadata"
            playsInline
            data-testid={`media-${item.id}-video`}
          />
        ) : (
          <img src={src} alt={item.caption || ""} data-testid={`media-${item.id}-img`} />
        )}

        {isVideo && (
          <span className="jp-media-type-badge" title="Video">▶</span>
        )}

        {isAdmin && (
          <button
            className="jp-del"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(item.id);
            }}
            title="Delete"
            data-testid={`media-${item.id}-delete`}
          >×</button>
        )}

        {showHighlightBadge && highlight && (
          <div className="jp-media-hl-badge" title={highlight}>
            <span style={{ color: GOLD }}>★</span> {highlight.substring(0, 40)}
            {highlight.length > 40 ? "…" : ""}
          </div>
        )}
      </div>

      <MediaMeta
        item={item}
        isAdmin={isAdmin}
        day={day}
        onUpdate={onUpdate}
      />
    </div>
  );
}

function MediaMeta({ item, isAdmin, day, onUpdate }) {
  const [editingCaption, setEditingCaption] = useState(false);
  const [draft, setDraft] = useState(item.caption || "");

  useEffect(() => {
    if (!editingCaption) setDraft(item.caption || "");
  }, [item.caption, editingCaption]);

  const commit = () => {
    const next = draft.trim();
    if (next !== (item.caption || "")) {
      onUpdate?.(item.id, { caption: next });
    }
    setEditingCaption(false);
  };

  const setHighlight = (val) => {
    const idx = val === "" ? null : parseInt(val, 10);
    onUpdate?.(item.id, { highlight_index: idx });
  };

  if (!isAdmin) {
    // Public: show caption text only if there is one
    if (!item.caption) return null;
    return <p className="jp-media-caption" data-testid={`media-${item.id}-caption`}>{item.caption}</p>;
  }

  return (
    <div className="jp-media-meta" onClick={(e) => e.stopPropagation()}>
      {editingCaption ? (
        <input
          autoFocus
          className="jp-edit-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              setDraft(item.caption || "");
              setEditingCaption(false);
            }
          }}
          placeholder="Add a caption…"
          data-testid={`media-${item.id}-caption-input`}
        />
      ) : (
        <button
          className="jp-media-caption-btn"
          onClick={() => setEditingCaption(true)}
          data-testid={`media-${item.id}-caption-btn`}
          title="Click to edit caption"
        >
          {item.caption || <span style={{ opacity: 0.5, fontStyle: "italic" }}>Add caption…</span>}
          <span className="jp-edit-pencil" aria-hidden>✎</span>
        </button>
      )}

      {day && (day.highlights || []).length > 0 && (
        <select
          className="jp-hl-select"
          value={item.highlight_index == null ? "" : String(item.highlight_index)}
          onChange={(e) => setHighlight(e.target.value)}
          data-testid={`media-${item.id}-highlight-select`}
          title="Link this photo/video to a highlight"
        >
          <option value="">No highlight link</option>
          {day.highlights.map((h, i) => (
            <option key={i} value={i}>
              ★ {h.length > 50 ? h.substring(0, 50) + "…" : h}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// UploadButton — admin uploader supporting photos AND videos, with optional
// pre-selected highlight_index. Streams as multipart/form-data to /api/media/upload
// and reports progress via a small inline bar.
// ---------------------------------------------------------------------------
export function MediaUploadButton({
  dayKey,
  highlightOptions = [],   // [{idx, text}]
  onUploaded,
  label = "Upload photos / videos",
  testId,
}) {
  const inputRef = useRef();
  const [progress, setProgress] = useState(null);   // {n, total, name, pct}
  const [chosenHighlight, setChosenHighlight] = useState("");

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setProgress({ n: 0, total: files.length, name: "", pct: 0 });

    let i = 0;
    for (const f of files) {
      i++;
      setProgress({ n: i, total: files.length, name: f.name, pct: 0 });
      const fd = new FormData();
      fd.append("day_key", dayKey);
      fd.append("file", f);
      if (chosenHighlight !== "") fd.append("highlight_index", chosenHighlight);
      try {
        await api.post("/media/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (!evt.total) return;
            const pct = Math.round((evt.loaded * 100) / evt.total);
            setProgress({ n: i, total: files.length, name: f.name, pct });
          },
        });
      } catch (err) {
        alert(`Failed to upload ${f.name}: ${err?.response?.data?.detail || err.message}`);
      }
    }
    setProgress(null);
    e.target.value = "";
    onUploaded?.();
  };

  return (
    <div className="jp-upload-block" data-testid={testId}>
      {highlightOptions.length > 0 && (
        <select
          className="jp-hl-select"
          value={chosenHighlight}
          onChange={(e) => setChosenHighlight(e.target.value)}
          data-testid={testId ? `${testId}-highlight-select` : undefined}
          title="Pre-link uploaded items to a highlight"
        >
          <option value="">No highlight link</option>
          {highlightOptions.map((h) => (
            <option key={h.idx} value={h.idx}>
              ★ {h.text.length > 50 ? h.text.substring(0, 50) + "…" : h.text}
            </option>
          ))}
        </select>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        style={{ display: "none" }}
        onChange={handleFiles}
        data-testid={testId ? `${testId}-input` : undefined}
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={progress !== null}
        className="jp-btn-primary inline"
        data-testid={testId ? `${testId}-btn` : undefined}
      >
        {progress
          ? `Uploading ${progress.n}/${progress.total} · ${progress.pct}%`
          : label}
      </button>

      {progress && (
        <div className="jp-progress" data-testid={testId ? `${testId}-progress` : undefined}>
          <div className="jp-progress-bar" style={{ width: `${progress.pct}%` }} />
          <div className="jp-progress-label">
            {progress.name} — {progress.pct}%
          </div>
        </div>
      )}

      <p className="jp-upload-hint">
        Photos up to 25 MB · Videos up to 100 MB. Multiple files supported.
      </p>
    </div>
  );
}
